import DataContext from 'UserRegistration/contexts/DataContext';
import Checkbox from 'commons/components/Checkbox';
import Badge from 'commons/components/Badge';
import React, { useContext, useEffect, useState } from 'react';
import classnames from 'classnames';
import MetsIcon from 'commons/components/MetsIcon';
import CloseIcon from '@mui/icons-material/Close';
import TreeSelect, { TreeSelectNodeData } from 'commons/components/TreeSelect';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Button from 'commons/components/Button';
import ConfigurationContext from 'UserRegistration/contexts/ConfigurationContext';
import { updateBillingRequired } from 'UserRegistration/contexts/configs/componentConfig';
import { useTranslation } from 'react-i18next';
import ChipList, { ChipListItem } from 'commons/components/ChipList';
import { formatPrice } from 'commons/resources/utils/numeric';

interface PCSubscriptionSelectorProps {
    disabled: boolean,
    packageSelectorData: any
}

function PCSubscriptionSelector(props: PCSubscriptionSelectorProps) {

    const { componentConfig, setComponentConfig } = useContext(ConfigurationContext);
    const { registrationData, setRegistrationData } = useContext(DataContext);
    const { packageSelectorData } = props;
    const { t, i18n }: any = useTranslation(componentConfig.i18nNamespaces);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedNodes, setSelectedNodes] = useState<any>([]);
    const classNames = classnames('pc-subscription-selector', { disabled: props.disabled }, { selected: registrationData.registrationModel.selectedSecondarySubscriptions.length });
    const [treeSelectData, setTreeSelectData] = useState<TreeSelectNodeData[]>([]);
    const [subscriptionItemsById, setSubscriptionItemsById] = useState<any>({});
    const [selectedItemsAtDialogOpen, setSelectedItemsAtDialogOpen] = useState<any[]>([]);
    const [doLaunchAction, setDoLaunchAction] = useState(true);

    // Any changes done to relatedSubscriptionItems need to follow the business rules defined in METS-3262 unless overridden by new business rules
    const [relatedSubscriptionItems, setRelatedSubscriptionItems] = useState<any>([]);

    /** Cost of PC based on confirmed user selection */
    let pcCost = 0;
    /** Cost of PC based on real time user selection */
    let livePcCost = 0;

    // Must be defined before the following two methods that use it.
    const calculateItemPrice = (item: any) => {
        if (registrationData.registrationModel.subscription.selectedFrequency == 'YEARLY') {
            // The business wants yearly prices to be displayed as a monthly value and therefore divided by 12.
            let yearlyPriceByMonth:number = item.totalPrices[registrationData.registrationModel.subscription.selectedFrequency] / 12;
            
            // Round to 2 decimal places UP.
            // https://stackoverflow.com/questions/11832914/how-to-round-to-at-most-2-decimal-places-if-necessary/
            var p = Math.pow(10, 2 || 0);
            var n = (yearlyPriceByMonth * p) * (1 - Math.sign(yearlyPriceByMonth) * Number.EPSILON);
            return Math.ceil(n) / p;
        } 
        
        // If not yearly, return the original price for the frequency.
        return item.totalPrices[registrationData.registrationModel.subscription.selectedFrequency];
    }

    const isSubscriptionItemSelected = (subscriptionItem: any) => {
        let isSelected = false;
        if (registrationData.registrationModel.selectedSecondarySubscriptions && registrationData.registrationModel.selectedSecondarySubscriptions.length) {
            isSelected = registrationData.registrationModel.selectedSecondarySubscriptions.some((item: { id: number; }) => item.id === subscriptionItem.id);
        }
        return isSelected;
    }

    if (registrationData.registrationModel.selectedSecondarySubscriptions.length || relatedSubscriptionItems.length) {
        let subItemsToCalculate:any[] = relatedSubscriptionItems;
        if (!subItemsToCalculate || !subItemsToCalculate.length) {
            subItemsToCalculate = registrationData.registrationModel.selectedSecondarySubscriptions;
        }

        subItemsToCalculate.forEach((item: any) => {
            pcCost += calculateItemPrice(item);
        });
    }

    // Calculate livePcCost.
    if (selectedNodes.length) {
        selectedNodes.forEach((item: any) => {
            livePcCost += calculateItemPrice(item);
        });
    }

    useEffect(() => {

        // Look for the PRIVATE_CONSTRUCTION (PC) subscription group.
        const pcSubscriptionGroup = packageSelectorData.availableSubscriptionItemGroups.find((item: any) => {
            return item.groupKey === 'PRIVATE_CONSTRUCTION';
        });

        // Look for the PRIVATE_CONSTRUCTION (PC) subscription item. We'll need that to build our dataset for this component.
        const pcSubscriptionItem = pcSubscriptionGroup?.subscriptionItems[0];


        // PC subscription item contains nested children (like a tree), we use byId to store them like a Map by ID for easy access.
        const byId: any = {};

        if (pcSubscriptionItem) {

            /* Loop through all of the nested PC subscription items and build our tree node data.
             * Since this data can be rebuilt if the selected billing frequency changes, we need to 
             * check if subscription items were previously selected by the user (using subscriptionItemsById).
             */
            const rootChildren = pcSubscriptionItem.children.map((item: any) => {
    
                byId[item.id] = Object.assign(item, {});
    
                const childNodes: TreeSelectNodeData[] = item.children.map((subItem: any) => {
                    byId[subItem.id] = Object.assign(subItem, {});
                    return {
                        childNodes: [],
                        value: subItem.id,
                        label: subItem.description + ' (' + formatPrice(calculateItemPrice(subItem), i18n.language, componentConfig.currencyCode) + ')',
                        maxSelectableChildren: subItem.maxSelectableChildren + 1,
                        isCollapsed: true,
                        isDisabled: !subItem.enabled || subscriptionItemsById[subItem.id]?.disabled || subscriptionItemsById[item.id]?.disabled,
                        isSelected: subscriptionItemsById[pcSubscriptionItem.id]?.selected || subscriptionItemsById[item.id]?.selected || subscriptionItemsById[subItem.id]?.selected,
                        isPreselected: isSubscriptionItemSelected(subItem)
                    };
                });
        
                return {
                    childNodes: childNodes,
                    value: item.id,
                    label: item.description + ' (' + formatPrice(calculateItemPrice(item), i18n.language, componentConfig.currencyCode) + ')',
                    maxSelectableChildren: item.maxSelectableChildren + 1,
                    isCollapsed: true,
                    isDisabled: !item.enabled || subscriptionItemsById[item.id]?.disabled,
                    isSelected: subscriptionItemsById[pcSubscriptionItem.id]?.selected || subscriptionItemsById[item.id]?.selected,
                    isPreselected: isSubscriptionItemSelected(item)
                };
        
            });
    
            byId[pcSubscriptionItem.id] = Object.assign(pcSubscriptionItem, {});
    
            setSubscriptionItemsById(byId);
            setTreeSelectData([{
                childNodes: rootChildren,
                maxSelectableChildren: pcSubscriptionItem.maxSelectableChildren + 1,
                value: pcSubscriptionItem.id,
                label: pcSubscriptionItem.description + ' (' + formatPrice(calculateItemPrice(pcSubscriptionItem), i18n.language, componentConfig.currencyCode) + ')',
                isCollapsed: false,
                isDisabled: !pcSubscriptionItem.enabled,
                isSelected: subscriptionItemsById[pcSubscriptionItem.id]?.selected,
                isPreselected: isSubscriptionItemSelected(pcSubscriptionItem),
            }]);

        }

        if (pcSubscriptionGroup.doLaunchAction && doLaunchAction) {
            setDoLaunchAction(false); // Launch action should only happen once.
            openModal();
        }

    }, [packageSelectorData, registrationData.registrationModel.subscription.selectedFrequency]);


    useEffect(() => {
        updateRelatedSubscriptionItems();
    }, [JSON.stringify(registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds)]);

    /**
     * Update the list of relatedSubscriptionItems or selectedSecondarySubscriptions based on the related items from the
     * selected Canadian Tenders subscription.The update will only occur when the user has not made any manual modifications
     * to their PC subscription (wasSecondarySubscriptionModified).
     */
    const updateRelatedSubscriptionItems = (registrationDataCopy:any = {}) => {

        if (!subscriptionItemsById || JSON.stringify(subscriptionItemsById) === "{}") {
            return;
        }

        registrationDataCopy = registrationDataCopy.registrationModel ? registrationDataCopy : Object.assign({}, registrationData);
        let newRelatedSubscriptionItems: any[] = [];

        if (!registrationDataCopy.registrationModel.wasSecondarySubscriptionModified && registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds && registrationDataCopy.registrationModel.selectedSubscriptionPackage) {
            let selectedSubItem = registrationDataCopy.registrationModel.selectedSubscriptionPackage.subscriptionItems.find((subItem: any) => {
                const selectedSubscriptionItemIds = registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds;
                // TODO selectedSubscriptionItemIds is either an array of strings or of numbers. This should be consistent.
                return selectedSubscriptionItemIds.includes(subItem.id.toString()) || selectedSubscriptionItemIds.includes(subItem.id);
            });

            if (selectedSubItem) {
                let subscriptionToCheck = getRelatedItems(selectedSubItem);
                
                // If there are selectedSecondarySubscriptions, then user must have checked the "Add PC" checkbox so update that list instead of the new related items list
                if (registrationDataCopy.registrationModel.selectedSecondarySubscriptions.length && subscriptionToCheck.length) {
                    registrationDataCopy.registrationModel.selectedSecondarySubscriptions = subscriptionToCheck
                } else {
                    newRelatedSubscriptionItems = subscriptionToCheck
                }
                
                // Checkmark all the PC subscription items for the PC dialog so that they are selected when the user opens the dialog
                if (subscriptionItemsById && subscriptionToCheck.length) {
                    selectNodesBySubscriptionItems(subscriptionToCheck);
                } else {
                    // If there's nothing to checkmark, then make sure that we clear any existing selections
                    clearSelections();
                }

                updateBillingRequired(registrationDataCopy, componentConfig, setComponentConfig);
            }
        }

        setRegistrationData(registrationDataCopy);
        setRelatedSubscriptionItems(newRelatedSubscriptionItems);
    }

    const getRelatedItems = (selectedSubItem: any) => {
        let relatedItems: any[] = [];
        if (selectedSubItem && selectedSubItem.relatedItemids && selectedSubItem.relatedItemids.length) {
            for (let relatedItemId of selectedSubItem.relatedItemids) {
                let item = subscriptionItemsById[relatedItemId];
                if (item) {
                    relatedItems.push(item);
                }
            }
        }
        return relatedItems;
    };

    const confirmSelection = () => {
        
        const registrationDataCopy = Object.assign({}, registrationData);

        // set flag that the user has manually selected PC
        registrationDataCopy.registrationModel.wasSecondarySubscriptionModified = true;

        registrationDataCopy.registrationModel.selectedSecondarySubscriptions = selectedNodes;

        updateBillingRequired(registrationDataCopy, componentConfig, setComponentConfig);

        setRegistrationData(registrationDataCopy);

        closeModal();

    };

    /**
     * Gets a flat map of the provided tree node and its children.
     * Key is the id of the subscription item.
     * Value is the node object itself.
     */
    const getFlatMapTreeNodes = (treeData: TreeSelectNodeData[] = [], inFlatMap:any = {}) => {
        let flatMap:any = inFlatMap !== {} ? inFlatMap : {};
        if (treeData.length === 0) {
            treeData = treeSelectData;
        }
        
        treeData.forEach((node: any) => {
            flatMap[node.value] = node;
            if (node.childNodes.length > 0) {
                flatMap = getFlatMapTreeNodes(node.childNodes, flatMap);
            }
        });
        return flatMap;
    }

    /**
     * Recursive method that can be called to collect all of the IDs of the children 
     * of a TreeSelectNodeData into a single array.
     * 
     * @param node 
     * @param ids 
     * @returns 
     */
    const collectChildIds = (node: TreeSelectNodeData, ids: any[]) => {
        
        node.childNodes.forEach((child: TreeSelectNodeData) => {

            ids.push(child.value);
                
            if (child.childNodes.length) {
                //ids = ids.concat(collectChildIds(child, ids));
                collectChildIds(child, ids);
            }

        });

        return ids;

    }

    /**
     * Add the supplied TreeSelectNodeData's associated subscription data into 
     * a given array.
     * 
     * @param node 
     * @param array 
     * @returns 
     */
    const addSelectedNode = (node: TreeSelectNodeData, array: any[]) => {

        // Push the selected node into the array.
        array.push(subscriptionItemsById[node.value]);
        // Mark the item as selected in case we need to rebuild the tree node data (change of billing freqency, etc).
        subscriptionItemsById[node.value].selected = true;

        const ids: any[] = [];
        collectChildIds(node, ids).forEach((id) => {
            subscriptionItemsById[id].disabled = true;
        });

        // Remove any previously added children from the array.
        array = array.filter((selectedNode: any) => {
            return !ids.includes(selectedNode.id);
        });

        return array;

    };

    /**
     * Select nodes for the PC dialog based on the provided list of subscription items.
     */
    const selectNodesBySubscriptionItems = (subscriptionItems: any[]) => {
        let flatMapTreeNodes = getFlatMapTreeNodes();

        if (flatMapTreeNodes === {}) {
            return;
        }

        clearSelections();
        
        let nodesToSelect: TreeSelectNodeData[] = [];
        for (let subscriptionItem of subscriptionItems) {
            let node: TreeSelectNodeData = flatMapTreeNodes[subscriptionItem.id];
            node.isSelected = true;
            checkUncheckChildren(node)
            nodesToSelect.push(node);
        }

        selectMultipleNodes(nodesToSelect, true);
    };

    /**
     * Helper to allow us to select multiple nodes at once. Used for the related items selection from METS-3262.
     */
    const selectMultipleNodes = (nodes: TreeSelectNodeData[], clearSelectedNodes: boolean = false) => {

        let newSelectedNodes = clearSelectedNodes ? [] : [...selectedNodes];

        for (let node of nodes) {
            const parent = node.parent;
            // If our tree node is now selected, we add it to our list of selected nodes.

            if (node.isSelected) {
                // Check up to 3 levels of nodes.
                if (parent && parent.parent && parent.parent.isSelected) {
                    newSelectedNodes = addSelectedNode(parent.parent, newSelectedNodes);
                } else if (parent && parent.isSelected) {
                    newSelectedNodes = addSelectedNode(parent, newSelectedNodes);
                } else {
                    newSelectedNodes = addSelectedNode(node, newSelectedNodes);
                }
            }
        }

        setSelectedNodes(newSelectedNodes);
    }

    /**
     * Callback that is fired when the user selects a node in the tree. This callback has the 
     * responsibility of selecting the subscription data associated to the nodes selected in the tree.
     * 
     * @param node 
     */
    const selectNode = (node: TreeSelectNodeData) => {

        const parent = node.parent;
        let newSelectedNodes = [...selectedNodes];

        // If our tree node is now selected, we add it to our list of selected nodes.
        if (node.isSelected) {

            // Check up to 3 levels of nodes.
            if (parent && parent.parent && parent.parent.isSelected) {
                newSelectedNodes = addSelectedNode(parent.parent, newSelectedNodes);
            } else if (parent && parent.isSelected) {
                newSelectedNodes = addSelectedNode(parent, newSelectedNodes);
            } else {
                newSelectedNodes = addSelectedNode(node, newSelectedNodes);
            }

        } else {

            // If the tree node is not selected, we must remove it from our list.

            // Remove the unselected node.
            newSelectedNodes = selectedNodes.filter((selectedNode: any) => {
                return selectedNode.id != node.value
            });

            const ids: any[] = [];

            subscriptionItemsById[node.value].selected = false;
            collectChildIds(node, ids).forEach((id) => {
                subscriptionItemsById[id].selected = false;
                subscriptionItemsById[id].disabled = false;
            });

            // Remove any children of the unselected node.
            newSelectedNodes = newSelectedNodes.filter((selectedNode: any) => {
                return !node.childNodes.map((child: any) => {
                    return child.id;
                }).includes(selectedNode.id);
            });

        }

        setSelectedNodes(newSelectedNodes);

    }

    /**
     * Recursive method that descends the given node's children and clears 
     * all selections.
     * 
     * @param node The node to operate on.
     * @param clearIf An optional function that is used to evaluate whether or not to clear the node.
     */
    const clearNodeSelection = (node: TreeSelectNodeData, clearIf: any = () => true) => {

        if (clearIf(node)) {
            node.isSelected = false;
            node.isDisabled = false;
            subscriptionItemsById[node.value].selected = false;
            subscriptionItemsById[node.value].disabled = false;
        }

        node.childNodes.forEach((child: TreeSelectNodeData) => {

            if (clearIf(child)) {
                child.isSelected = false;
                child.isDisabled = false;
                subscriptionItemsById[child.value].selected = false;
                subscriptionItemsById[child.value].disabled = false;
            }

            if (child.childNodes.length) {
                clearNodeSelection(child, clearIf);
            }

        });

    };

    /**
     * Clears the selected tree nodes.
     */
    const clearSelections = () => {

        const newTreeSelectData = treeSelectData.map((node: TreeSelectNodeData) => {
            return Object.assign(node, {});
        });

        newTreeSelectData.forEach((node: TreeSelectNodeData) => {
            clearNodeSelection(node);
        });

        setTreeSelectData(newTreeSelectData);
        setSelectedNodes([]);

    };

    /**
     * Removes the subscription item associated with the chip that was clicked.
     * 
     * @param item 
     * @param event 
     */
    const onChipClick = (item: ChipListItem, event: React.MouseEvent) => {

        let newSelectedNodes = [...selectedNodes];
        const registrationDataCopy = Object.assign({}, registrationData);
        const newTreeSelectData = treeSelectData.map((node: TreeSelectNodeData) => {
            return Object.assign(node, {});
        });

        newTreeSelectData.forEach((node: TreeSelectNodeData) => {
            clearNodeSelection(node, (n: TreeSelectNodeData) => {

                if (n.value === item.value) {
                    return true;
                }

                let parent = n.parent;

                while (parent != null) {
                    if (parent.value === item.value) {
                        return true;
                    }
                    parent = parent.parent;
                }

                return false;

            });
        });

        registrationDataCopy.registrationModel.selectedSecondarySubscriptions = registrationDataCopy.registrationModel.selectedSecondarySubscriptions.filter((subscriptionItem: any) => {
            return subscriptionItem.id !== item.value;
        });

        newSelectedNodes = newSelectedNodes.filter((selectedNode: any) => {
            return selectedNode.id !== item.value;
        });
        setSelectedNodes(newSelectedNodes);

        registrationDataCopy.registrationModel.wasSecondarySubscriptionModified = true;
        // If selectedSecondarySubscriptions is empty, then we want to bring back the greyed-out pricing by updating RelatedSubscriptionItems list 
        if (registrationDataCopy.registrationModel.selectedSecondarySubscriptions.length === 0) {
            registrationDataCopy.registrationModel.wasSecondarySubscriptionModified = false;
            // NOTE: Must be called after setSelectedNodes, as it could modify the selected nodes based on related items
            updateRelatedSubscriptionItems(registrationDataCopy);
        }

        updateBillingRequired(registrationDataCopy, componentConfig, setComponentConfig);
        setRegistrationData(registrationDataCopy);
        setTreeSelectData(newTreeSelectData);

        event.stopPropagation();

    };

    const openModal = () => {

        // If we have related subscription items, then set the related subscription items as selected and don't open the dialog
        if (relatedSubscriptionItems && relatedSubscriptionItems.length > 0) {
            const registrationDataCopy = Object.assign({}, registrationData);
            registrationDataCopy.registrationModel.selectedSecondarySubscriptions = registrationDataCopy.registrationModel.selectedSecondarySubscriptions.concat(relatedSubscriptionItems);
            updateBillingRequired(registrationDataCopy, componentConfig, setComponentConfig);
            setRelatedSubscriptionItems([]);
            setRegistrationData(registrationDataCopy);
        } 
        else {
            if (!props.disabled) {
                setIsModalOpen(true);
            }
            setSelectedItemsAtDialogOpen([...selectedNodes.map((node: any) => node.id)]);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
    }

    /**
     * Method that reverts the selection of subscription tree nodes back to what the user had already previously confirmed since the 
     * last time that they opened the PC Dialog. To be used when the user hits Cancel on the PC dialog.
     */
    const cancelAndClose = () => {

        // The node subscription IDs previously confirmed by the user.
        const confirmedIds = registrationData.registrationModel.selectedSecondarySubscriptions.map((item: any) => item.id);
        // Out of all currently selected nodes, first strip out any that are not in the confirmedIds list.
        const selectedNodesToKeep = selectedNodes.filter((node: any) => confirmedIds.includes(node.id));
        const newTreeSelectData = treeSelectData.map((node: TreeSelectNodeData) => {
            return Object.assign(node, {});
        });

        // For each node in the tree, clear it or select it.
        newTreeSelectData.forEach((node: TreeSelectNodeData) => {
            // Clear any nodes that were unconfirmed.
            clearNodeSelection(node, (n: TreeSelectNodeData) => { return !confirmedIds.includes(n.value) && !confirmedIds.includes(n.parent?.value) && !confirmedIds.includes(n.parent?.parent?.value) && n.isSelected; });
            // Reselect any nodes that were unchecked by the user, but not confirmed.
            selectNodeRecursive(node, selectedNodesToKeep, (n: TreeSelectNodeData) => { return confirmedIds.includes(n.value) && !n.isSelected; });
        });

        // Update the selected nodes and set them back to what was there previously.
        setSelectedNodes(selectedNodesToKeep);
        setTreeSelectData(newTreeSelectData);

        // Finally, close the PC dialog.
        closeModal();

    }

    const selectNodeRecursive = (node: TreeSelectNodeData, selectedNodes: any[], selectIf: any = () => true) => {

        if (selectIf(node)) {
            node.isSelected = true;
            addSelectedNode(node, selectedNodes);
            checkUncheckChildren(node, true);
        } else {

            node.childNodes.forEach((child) => {
                selectNodeRecursive(child, selectedNodes, selectIf);
            });

        }

    }

    /**
     * Recursively checks or unchecks the children of a given TreeSelectNodeData.
     * 
     * @param node 
     * @param check 
     */
    const checkUncheckChildren = (node: TreeSelectNodeData, check = true) => {
        node.childNodes.forEach((child) => {
            child.isSelected = check;
            child.isDisabled = check;
            checkUncheckChildren(child, check);
        })
    }

    // Returns whether or not the user has made any changes since last opening the PC dialog.
    const hasUserMadeChange = (): boolean => {

        const currentlySelectedNodes = selectedNodes.map((node: any) => node.id);

        const result = selectedItemsAtDialogOpen.length !== currentlySelectedNodes.length || (selectedItemsAtDialogOpen.length !== 0 && currentlySelectedNodes.length !== 0 && currentlySelectedNodes.every((id: any) => !selectedItemsAtDialogOpen.includes(id)));

        return result;

    }

    /**
     * (METS-4117) If there are any selected secondary subscriptions, then unselect to uncheck the PC subscription.
     * If there are no selected secondary subscriptions, then attempt to open the modal dialog.
     */
    const togglePcCheckbox = () => {
        const registrationDataCopy = Object.assign({}, registrationData);

        if (registrationDataCopy.registrationModel.selectedSecondarySubscriptions.length) {
            registrationDataCopy.registrationModel.selectedSecondarySubscriptions = [];
            registrationDataCopy.registrationModel.wasSecondarySubscriptionModified = false;
            updateRelatedSubscriptionItems(registrationDataCopy);
            setRegistrationData(registrationDataCopy);
        } else {
            openModal();
        }
    }

    // Prevents event propagation to avoid trigger certain types of events twice.
    const stopEventPropagation = (event: React.SyntheticEvent) => {
        event.stopPropagation();
    }

    return(
        <>
            <div className={classNames} >
                <div className="pc-checkbox" onClick={togglePcCheckbox} >
                    <Checkbox onChange={stopEventPropagation} onClick={stopEventPropagation} checked={registrationData.registrationModel.selectedSecondarySubscriptions.length} />
                </div>
                <div className="pc-content" onClick={() => { if (!registrationData.registrationModel.selectedSecondarySubscriptions.length) openModal(); }}>
                    <div className="pc-content-top">
                        <div>
                            <div className="pc-title">
                                <h3>{t('pc.title')}</h3>
                            </div>
                            <p>{t('pc.desc')}</p>
                        </div>
                        <div>
                            {pcCost > 0 && <Badge className={registrationData.registrationModel.selectedSecondarySubscriptions.length ? "green" : "grey"} label={'+' + formatPrice(pcCost, i18n.language, componentConfig.currencyCode) + '/month'} />}
                        </div>
                    </div>
                    {registrationData.registrationModel.selectedSecondarySubscriptions.length > 0 &&
                    <div className="modify-subscription">
                        <span className="bold">{t('pc.selectRegionalPackage', { numberOfRegions: registrationData.registrationModel.selectedSecondarySubscriptions.length })} <a href="javascript:" onClick={openModal}>{t('pc.addChangeRegions')}</a></span>
                    </div>}
                    {registrationData.registrationModel.selectedSecondarySubscriptions.length > 0 && 
                    <ChipList
                        items={registrationData.registrationModel.selectedSecondarySubscriptions.map((node: any) => { return { label: node.description, value: node.id } })}
                        onChipClick={onChipClick}
                    />}
                </div>

            </div>
            <Dialog className="pc-subscription-dialog" keepMounted open={isModalOpen} onClose={cancelAndClose}>
                <DialogTitle>
                    <MetsIcon key="svg_statesMap" name="svg_statesMap" />
                    {t('pc.selectRegions')}
                    <CloseIcon height="24" width="24" onClick={cancelAndClose} />
                </DialogTitle>
                <DialogContent>
                    <div className="selection-container">
                        <div className="subscription-options">
                            <div className="title">
                                <span>{t('pc.treeLegend')}</span>
                                <a className="clear-all-link" href="javascript:" onClick={clearSelections}>{t('clearAll')}</a>
                            </div>
                            {treeSelectData && <TreeSelect data={treeSelectData} onSelect={selectNode} />}
                        </div>
                        <div className="subscription-price">
                            <span className="title">{t('subscriptionPrice')}</span>
                            <div className="price-display">
                                <span className="price">{formatPrice(livePcCost, i18n.language, componentConfig.currencyCode)}</span>/{t('month')}<br/>
                                {t('frequency.' + registrationData.registrationModel.subscription.selectedFrequency + '.description')}
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <a className="cancel-link" href="javascript:" onClick={cancelAndClose}>{t('cancel')}</a>
                    <Button className="clear-all-button dark" onClick={clearSelections} label={t('clearAll')}></Button>
                    <Button className="confirm-button" disabled={!hasUserMadeChange()} onClick={confirmSelection} label={t('pc.confirmSelection', { numberOfRegions: selectedNodes.length })}></Button>
                </DialogActions>
            </Dialog>
        </>
    );

}

export default PCSubscriptionSelector;
